import type { ComponentProps } from 'react';
import cn from '@/lib/cn';
import { Link } from '@/lib/i18n/navigation';
import BrandGradient from './BrandGradient';

export type BaseProps = {
  /** Color scheme of the button */
  appearance?: 'dark' | 'light';
  /** Text for the button */
  children: string | string[];
  /** Additional CSS class(es) for the button */
  className?: string;
  /** Kind of the button */
  kind?: 'primary' | 'secondary' | 'tertiary';
  /** Whether to show the icon or not */
  showIcon?: boolean;
  /** Size of the button */
  size?: 'sm' | 'md';
};

type LinkButtonProps = Omit<
  ComponentProps<typeof Link>,
  'children' | 'href'
> & {
  /**
   * The path or URL the button navigates to – if not provided, the button will
   * be an interactive button (`button` element)
   * */
  href: string;
};

type InteractiveButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: never;
};

export default function Button({
  appearance = 'dark',
  children,
  className = undefined,
  href = undefined,
  kind = 'primary',
  showIcon = true,
  size = 'md',
  ...props
}: BaseProps & (LinkButtonProps | InteractiveButtonProps)) {
  /* Button classes */

  const buttonClasses = [
    'inline-flex items-center justify-center gap-2 transition-colors group/button',
  ];

  // Padding

  if (size === 'md' && kind !== 'tertiary') {
    buttonClasses.push('px-6');
  }

  if (size === 'sm' && kind !== 'tertiary') {
    buttonClasses.push('px-4');
  }

  if (size === 'md' && kind === 'primary') {
    buttonClasses.push('py-3');
  }

  if (size === 'sm' && kind === 'primary') {
    buttonClasses.push('py-2.5');
  }

  if (size === 'md' && kind === 'secondary') {
    buttonClasses.push('py-2.75');
  }

  if (size === 'sm' && kind === 'secondary') {
    buttonClasses.push('py-2.25');
  }

  if (size === 'md' && kind === 'tertiary') {
    buttonClasses.push('pt-3 pb-2.5');
  }

  if (size === 'sm' && kind === 'tertiary') {
    buttonClasses.push('pt-2.5 pb-2');
  }

  // Border radius

  if (kind !== 'tertiary') {
    buttonClasses.push('rounded-full');
  }

  // Background

  if (kind === 'primary' && appearance === 'dark') {
    buttonClasses.push('relative isolate overflow-hidden');
  }

  if (kind === 'primary' && appearance === 'light') {
    buttonClasses.push('bg-white ring ring-black/8 hover:ring-black/16');
  }

  if (kind === 'secondary' && appearance === 'dark') {
    buttonClasses.push(
      'bg-black/8 border border-black/8 hover:border-black/16',
    );
  }

  if (kind === 'secondary' && appearance === 'light') {
    buttonClasses.push(
      'bg-white/10 border border-white/8 hover:border-white/16',
    );
  }

  if (kind === 'tertiary' && appearance === 'dark') {
    buttonClasses.push(
      'border-b-2 border-neutral-80 hover:border-neutral-80/60',
    );
  }

  if (kind === 'tertiary' && appearance === 'light') {
    buttonClasses.push('border-b-2 border-white/20 hover:border-white/40');
  }

  // Text color

  if (
    (kind === 'primary' && appearance === 'dark') ||
    (kind !== 'primary' && appearance === 'light')
  ) {
    buttonClasses.push('text-white');
  } else {
    buttonClasses.push('text-signal-blue-10');
  }

  // Font size

  if (size === 'md') {
    buttonClasses.push('text-sm');
  }

  if (size === 'sm') {
    buttonClasses.push('text-xs');
  }

  // Font weight

  if (kind === 'primary') {
    buttonClasses.push('font-semibold');
  } else {
    buttonClasses.push('font-medium');
  }

  /* Icon classes */

  const iconClasses = [
    'rounded-sm flex items-center justify-center transition',
  ];

  if (size === 'sm') {
    iconClasses.push('size-4 group-hover/button:translate-x-0.5');
  }

  if (size === 'md') {
    iconClasses.push('size-5 group-hover/button:translate-x-0.75');
  }

  if (
    (kind === 'primary' && appearance === 'dark') ||
    (kind !== 'primary' && appearance === 'light')
  ) {
    iconClasses.push('bg-white/10 group-hover/button:bg-white/15');
  } else {
    iconClasses.push('bg-black/8 group-hover/button:bg-black/10');
  }

  /* Icon */

  const Icon = () => {
    if (!showIcon) return null;

    return (
      <span className={iconClasses.join(' ')}>
        <svg
          className={cn('transition-transform delay-50', {
            'group-hover/button:translate-x-0.5': size === 'md',
            'group-hover/button:translate-x-0.25': size === 'sm',
          })}
          xmlns="http://www.w3.org/2000/svg"
          width="4"
          height="7"
          viewBox="0 0 4 7"
          fill="currentColor"
        >
          <path d="M.17.18C.4-.06.77-.06,1,.18l2.82,2.9c.23.24.23.62,0,.85l-2.82,2.9c-.23.24-.6.24-.83,0-.23-.24-.23-.62,0-.85l2.41-2.47L.17,1.03C-.06.79-.06.41.17.18Z" />
        </svg>
      </span>
    );
  };

  /* Render */

  if (!href) {
    return (
      <button
        className={cn(buttonClasses.join(' '), className)}
        type="button"
        {...(props as InteractiveButtonProps)}
      >
        {children}

        <Icon />

        {kind === 'primary' && appearance === 'dark' && (
          <BrandGradient className="transition-transform duration-500 group-hover/button:scale-200" />
        )}
      </button>
    );
  }

  return (
    <Link
      className={cn(buttonClasses.join(' '), className)}
      {...(props as LinkButtonProps)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      href={href as any}
    >
      {children}

      <Icon />

      {kind === 'primary' && appearance === 'dark' && (
        <BrandGradient className="transition-transform duration-500 group-hover/button:scale-200" />
      )}
    </Link>
  );
}
